import * as React from 'react';

interface ShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ShortcutsHelp: React.FC<ShortcutsHelpProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const shortcuts = [
    { key: 'B', description: 'Select Brush tool' },
    { key: 'E', description: 'Select Eraser tool' },
    { key: 'S', description: 'Select Selection tool' },
    { key: 'G', description: 'Toggle grid visibility' },
    { key: 'Ctrl + Z', description: 'Undo' },
    { key: 'Ctrl + Y', description: 'Redo' },
    { key: 'Shift + Drag', description: 'Pan camera' },
    { key: 'Alt + Click', description: 'Eyedropper (pick tile from canvas)' },
    { key: 'T', description: 'Test-play level in-place (no reload)' },
    { key: 'Escape', description: 'Exit test-play → return to editor' },
    { key: 'Mouse Wheel', description: 'Zoom in/out (to cursor)' },
    { key: '1-5', description: 'Select brush size 1-5' },
  ];

  const toolShortcuts = [
    { tool: 'Brush', shortcut: 'B', icon: '🖌️' },
    { tool: 'Eraser', shortcut: 'E', icon: '🧽' },
    { tool: 'Select', shortcut: 'S', icon: '🖱️' },
    { tool: 'Fill', shortcut: 'F', icon: '🪣' },
    { tool: 'Rectangle', shortcut: 'R', icon: '⬜' },
    { tool: 'Line', shortcut: 'L', icon: '📏' },
    { tool: 'Eyedropper', shortcut: 'I', icon: '💧' },
    { tool: 'Entity', shortcut: 'N', icon: '👤' },
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      pointerEvents: 'auto' // Ensure overlay receives clicks
    }} onClick={onClose}>
      <div style={{
        backgroundColor: '#2c3e50',
        color: 'white',
        padding: '30px',
        borderRadius: '10px',
        maxWidth: '800px',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            ⌨️ Keyboard Shortcuts
          </h2>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            Close
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
          {/* Tool Shortcuts */}
          <div>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#3498db' }}>
              🛠️ Tool Shortcuts
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {toolShortcuts.map(({ tool, shortcut, icon }) => (
                <div key={tool} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  padding: '12px',
                  backgroundColor: '#34495e',
                  borderRadius: '6px'
                }}>
                  <div style={{ fontSize: '20px' }}>{icon}</div>
                  <div style={{ flex: 1, fontSize: '16px' }}>{tool}</div>
                  <div style={{
                    padding: '6px 12px',
                    backgroundColor: '#3498db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    minWidth: '40px',
                    textAlign: 'center'
                  }}>
                    {shortcut}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* General Shortcuts */}
          <div>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#27ae60' }}>
              ⚡ General Shortcuts
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {shortcuts.map(({ key, description }) => (
                <div key={key} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: '#34495e',
                  borderRadius: '6px'
                }}>
                  <div style={{ fontSize: '16px' }}>{description}</div>
                  <div style={{
                    padding: '6px 12px',
                    backgroundColor: '#27ae60',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    fontFamily: 'monospace'
                  }}>
                    {key}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#34495e', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#f39c12' }}>
            💡 Pro Tips
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Shift + Drag:</strong> Pan the camera
            </div>
            <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
              <strong>Middle Click:</strong> Quick pan camera
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};